import { LatLng } from '../types/map';
import { Player, Agent } from '../types/game';
import { PathResult } from './pathfinding';
import { tuning } from '../data/tuning';
import { agentHasPassive } from '../data/agentPassives';

/**
 * Detection system — checks if a target is within a viewer's vision cone.
 * Used for both player-sees-agent and agent-sees-player checks.
 */

function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Calculate the bearing (in degrees, 0=north, 90=east) from one point to another.
 */
export function bearingDeg(from: LatLng, to: LatLng): number {
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  const bearingRad = Math.atan2(y, x);
  return ((bearingRad * 180) / Math.PI + 360) % 360;
}

/**
 * Check if a target point is within a vision cone.
 *
 * @param viewerPos - Position of the viewer
 * @param facingDeg - Viewer's facing direction in degrees (0=north, 90=east)
 * @param coneWidthDeg - Total cone width in degrees (e.g. 90 = 45° each side)
 * @param coneRange - Maximum range in meters
 * @param targetPos - Position to check
 * @returns true if target is within the cone
 */
export function isInVisionCone(
  viewerPos: LatLng,
  facingDeg: number,
  coneWidthDeg: number,
  coneRange: number,
  targetPos: LatLng,
): boolean {
  // Check distance first (cheap)
  const dist = haversineDistance(viewerPos.lat, viewerPos.lng, targetPos.lat, targetPos.lng);
  if (dist > coneRange) return false;

  // 360° cone = full circle, always in cone if within range
  if (coneWidthDeg >= 360) return true;

  // Check angle
  const bearingToTarget = bearingDeg(viewerPos, targetPos);
  const halfWidth = coneWidthDeg / 2;

  // Angular difference (handles wrapping around 360)
  let diff = Math.abs(bearingToTarget - facingDeg);
  if (diff > 180) diff = 360 - diff;

  return diff <= halfWidth;
}

/**
 * Check if the player can see the agent in their vision cone.
 * Returns true if the agent is visible (within cone and range).
 *
 * A player hiding in a building cannot see the agent (no vision cone).
 */
export function playerCanSeeAgent(player: Player, agent: Agent): boolean {
  if (player.isInBuilding) return false;

  const hasBinoculars = player.items.some(i => i.id.startsWith('binoculars'));
  const hasSniperRifle = player.items.some(i => i.id.startsWith('sniper'));
  let range = tuning.visionConeRange;

  if (player.isElevated && hasSniperRifle) {
    // Rooftop + sniper rifle: maximum range — shoot mode
    range = tuning.sniperRifleRange;
  } else if (player.isElevated) {
    // Rooftop: base 2× range, binoculars bump to 4×
    range *= hasBinoculars ? 4 : tuning.rooftopRangeMultiplier;
  } else if (hasBinoculars) {
    // Ground level: binoculars give 2× range
    range *= 2;
  }

  // Deep Cover passive: hard-cap player detection range at 100m regardless
  // of binoculars, rooftop, or sniper. The player's cone still renders at
  // full range visually, but detection only fires within 100m.
  if (agentHasPassive(agent, 'deepCover')) {
    range = Math.min(range, 100);
  }

  // Cone width:
  //  - Instinct (360°) overrides everything
  //  - Sniper on rooftop: tight cone (90°, or 110° with binoculars to help spot)
  //  - Rooftop: wider cone (120°)
  //  - Otherwise: player's current cone width
  let coneWidth = player.visionConeWidth;
  if (player.visionConeWidth < 360 && player.isElevated) {
    if (hasSniperRifle) {
      coneWidth = hasBinoculars ? tuning.sniperConeAngleWithBinoculars : tuning.sniperConeAngle;
    } else {
      coneWidth = tuning.rooftopConeAngle;
    }
  }

  return isInVisionCone(
    player.position,
    player.facing,
    coneWidth,
    range,
    agent.position,
  );
}

/**
 * Check if the agent can see the player.
 * The agent's facing is derived from their movement direction (stateData).
 * The agent uses the same cone width and range as the player.
 *
 * A player hiding in a building is not visible.
 *
 * NOTE: Deep Cover used to be a player passive that capped agent detection
 * range at 100m. It has been reworked into an agent-only passive that
 * instead caps the PLAYER'S effective range against specific agents —
 * see src/data/agentPassives.ts. The runtime hook for agent-side Deep
 * Cover lives in playerCanSeeAgent (Phase 2 adds it there), not here.
 */
export function agentCanSeePlayer(agent: Agent, player: Player): boolean {
  if (player.isInBuilding) return false;

  // Agent facing is stored directly from its last movement direction
  const agentFacing = agent.facing ?? 0;
  const hasSniper = agent.equipment.some(i => i.id.startsWith('sniper'));

  // Elevation-aware range + cone. Sniper on rooftop gets max range + tight cone.
  // Non-sniper on rooftop gets 2× range + 120° cone. Ground level = base.
  let range = tuning.visionConeRange;
  let coneWidth = agent.instinctActive ? 360 : tuning.visionConeAngle;
  if (agent.isElevated) {
    if (hasSniper) {
      range = tuning.sniperRifleRange;
      if (!agent.instinctActive) coneWidth = tuning.sniperConeAngle;
    } else {
      range *= tuning.rooftopRangeMultiplier;
      if (!agent.instinctActive) coneWidth = tuning.rooftopConeAngle;
    }
  }

  // Eagle Eye passive: +50% vision range (stacks with elevation)
  if (agentHasPassive(agent, 'eagleEye')) {
    range *= 1.5;
  }

  // Use rooftop position as view origin when elevated
  const viewPos = agent.isElevated && agent.rooftopPosition
    ? agent.rooftopPosition
    : agent.position;

  return isInVisionCone(
    viewPos,
    agentFacing,
    coneWidth,
    range,
    player.position,
  );
}

// --- Mid-movement detection ---

const SAMPLE_INTERVAL_M = 20; // check every 20m along the path

/**
 * Interpolate between two [lng, lat] points by a ratio t (0..1).
 */
function interpolateCoord(
  a: [number, number],
  b: [number, number],
  t: number,
): [number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/**
 * Sample positions along a coordinate path at roughly SAMPLE_INTERVAL_M spacing.
 * Returns array of [lng, lat] sample points plus the segment index they fall on.
 */
function samplePath(
  coordinates: [number, number][],
): { coord: [number, number]; segIndex: number; segT: number }[] {
  const samples: { coord: [number, number]; segIndex: number; segT: number }[] = [];
  if (coordinates.length < 2) return samples;

  let accumulated = 0;
  let nextSampleAt = SAMPLE_INTERVAL_M;

  for (let i = 0; i < coordinates.length - 1; i++) {
    const a = coordinates[i];
    const b = coordinates[i + 1];
    const segLen = haversineDistance(a[1], a[0], b[1], b[0]);
    const segStart = accumulated;

    while (nextSampleAt <= segStart + segLen) {
      const tInSeg = segLen > 0 ? (nextSampleAt - segStart) / segLen : 0;
      samples.push({
        coord: interpolateCoord(a, b, Math.min(1, tInSeg)),
        segIndex: i,
        segT: Math.min(1, tInSeg),
      });
      nextSampleAt += SAMPLE_INTERVAL_M;
    }
    accumulated += segLen;
  }

  // Always include the endpoint as the final sample so mid-path detection
  // checks the agent's actual stopping position with direction-of-travel.
  // Without this, the last 0-20m of path goes unchecked, and the agent can
  // "teleport" past the player before post-move detection fires combat.
  const lastSegIdx = coordinates.length - 2;
  const endCoord = coordinates[coordinates.length - 1];
  const lastSample = samples[samples.length - 1];
  const endAlreadySampled =
    lastSample &&
    lastSample.coord[0] === endCoord[0] &&
    lastSample.coord[1] === endCoord[1];
  if (lastSegIdx >= 0 && !endAlreadySampled) {
    samples.push({
      coord: endCoord,
      segIndex: lastSegIdx,
      segT: 1,
    });
  }

  return samples;
}

export interface MidPathDetection {
  /** Index into coordinates where detection happened */
  truncateAtSegment: number;
  truncateT: number; // 0..1 within that segment
  truncatedCoord: [number, number]; // [lng, lat] where to stop
  playerSeesAgent: boolean;
  agentSeesPlayer: boolean;
}

/**
 * Check if the player detects or is detected by the agent while moving along a path.
 * The player's facing updates to follow the direction of travel at each sample.
 *
 * Returns null if no detection occurs, otherwise returns the truncation point.
 */
export function checkPlayerMidPathDetection(
  path: PathResult,
  player: Player,
  agent: Agent,
): MidPathDetection | null {
  if (player.isInBuilding) return null;
  if (path.coordinates.length < 2) return null;

  const samples = samplePath(path.coordinates);
  const hasBinoculars = player.items.some(i => i.id.startsWith('binoculars'));
  let playerRange = tuning.visionConeRange;
  if (hasBinoculars) playerRange *= player.isElevated ? 4 : 2;
  // Deep Cover: cap player detection range at 100m against this agent
  if (agentHasPassive(agent, 'deepCover')) {
    playerRange = Math.min(playerRange, 100);
  }

  // Elevation-aware agent range + cone for mid-path detection
  const hasSniper = agent.equipment.some(i => i.id.startsWith('sniper'));
  let agentRange = tuning.visionConeRange;
  let agentCone = tuning.visionConeAngle;
  if (agent.isElevated) {
    if (hasSniper) { agentRange = tuning.sniperRifleRange; agentCone = tuning.sniperConeAngle; }
    else { agentRange *= tuning.rooftopRangeMultiplier; agentCone = tuning.rooftopConeAngle; }
  }
  if (agentHasPassive(agent, 'eagleEye')) agentRange *= 1.5;

  const agentViewPos = agent.isElevated && agent.rooftopPosition
    ? agent.rooftopPosition : agent.position;

  // Agent facing (static during player's move)
  const agentFacing = agent.facing ?? 0;

  // Walk along the path, computing the player's facing from travel direction
  let prevCoord = path.coordinates[0];

  for (const sample of samples) {
    const playerPos: LatLng = { lat: sample.coord[1], lng: sample.coord[0] };
    const facing = bearingDeg(
      { lat: prevCoord[1], lng: prevCoord[0] },
      playerPos,
    );

    // Player sees agent? Use agent's effective position (rooftop or street)
    const playerSees = isInVisionCone(
      playerPos, facing, player.visionConeWidth, playerRange, agentViewPos,
    );

    // Agent sees player at this new position?
    const agentSees = isInVisionCone(
      agentViewPos, agentFacing, agentCone, agentRange, playerPos,
    );

    if (playerSees || agentSees) {
      return {
        truncateAtSegment: sample.segIndex,
        truncateT: sample.segT,
        truncatedCoord: sample.coord,
        playerSeesAgent: playerSees,
        agentSeesPlayer: agentSees,
      };
    }

    prevCoord = sample.coord;
  }

  return null;
}

/**
 * Check if the agent detects or is detected by the player while the agent moves along a path.
 * The agent's facing updates to follow its direction of travel.
 *
 * Returns null if no detection occurs.
 */
export function checkAgentMidPathDetection(
  pathCoordinates: [number, number][],
  player: Player,
  agent: Agent,
): MidPathDetection | null {
  if (player.isInBuilding) return null;
  if (pathCoordinates.length < 2) return null;

  const samples = samplePath(pathCoordinates);
  const hasBinoculars = player.items.some(i => i.id.startsWith('binoculars'));
  let playerRange = tuning.visionConeRange;
  if (hasBinoculars) playerRange *= player.isElevated ? 4 : 2;
  if (agentHasPassive(agent, 'deepCover')) playerRange = Math.min(playerRange, 100);

  // Agent walking range (Eagle Eye stacks)
  let agentRange = tuning.visionConeRange;
  if (agentHasPassive(agent, 'eagleEye')) agentRange *= 1.5;

  let prevCoord = pathCoordinates[0];

  for (const sample of samples) {
    const agentPos: LatLng = { lat: sample.coord[1], lng: sample.coord[0] };
    // Agent facing = direction of travel
    const agentFacing = bearingDeg(
      { lat: prevCoord[1], lng: prevCoord[0] },
      agentPos,
    );

    // Agent sees player? This is the key check — agent stops when it spots the player.
    const agentSees = isInVisionCone(
      agentPos, agentFacing, tuning.visionConeAngle, agentRange, player.position,
    );

    // Only stop the agent's movement when IT detects the player (triggers engage/combat).
    // Don't stop just because the player can see the agent — the player seeing
    // the agent is resolved during the player's turn. Stopping here was causing the
    // agent to freeze ~20m per turn whenever the player looked in its direction.
    if (agentSees) {
      const playerSees = isInVisionCone(
        player.position, player.facing, player.visionConeWidth, playerRange, agentPos,
      );
      return {
        truncateAtSegment: sample.segIndex,
        truncateT: sample.segT,
        truncatedCoord: sample.coord,
        playerSeesAgent: playerSees,
        agentSeesPlayer: true,
      };
    }

    prevCoord = sample.coord;
  }

  return null;
}
